/**
 * Modo `modal` — monta el iframe dentro de un overlay centrado que cubre el
 * viewport (R2, §8 plan — modal UI / §11 a11y).
 *
 * Espejo de `mountEmbedded` pero con overlay. El overlay se apenda a
 * `document.body` (no al contenedor del host) para cubrir todo el viewport
 * independientemente del layout del sitio. El `iframe` recibido se mueve
 * dentro del wrapper (no se crea uno nuevo) para reutilizar el `Messenger`
 * ya conectado en `bricks.ts`.
 *
 * Cierre: `Escape`, backdrop click y `finva:close` (emitido por el iframe cuando
 * el usuario toca la X que la app renderiza dentro del contenido — R3 cablea
 * `emitClose`). Ya no hay barra-header con X en el chrome del modal.
 *
 * Accesibilidad:
 *  - `role="dialog"` + `aria-modal="true"` en el overlay.
 *  - Focus trap sobre la tarjeta + restauración del foco previo al cerrar.
 *  - Scroll-lock del `body` mientras el modal está abierto.
 *  - `prefers-reduced-motion`: sin animación (estado final aplica igual).
 *
 * El overlay permanece en el DOM tras `close()` (solo oculto con
 * `display:none`); se remueve recién en `unmount()`. Tras `unmount`,
 * `open()/close()` son no-op.
 */
import type { Messenger } from '../transport/messenger';
import { emptyContainer } from '../util/dom';
import { createFocusTrap } from '../util/focus-trap';
import { injectStyles } from '../ui/styles';

/** Altura inicial razonable del iframe antes del primer `finva:resize`. */
const INITIAL_MIN_HEIGHT = 420;
/** Duración de la transición de cierre (fallback con timer si transitionend no dispara). */
const CLOSE_TRANSITION_MS = 150;

export interface ModalMount {
  /** `<iframe>` montado (null tras `unmount`). */
  readonly iframe: HTMLIFrameElement | null;
  /** Abre el overlay (no-op si está disposed o ya abierto). */
  open(): void;
  /** Cierra el overlay (no-op si está disposed o ya cerrado). */
  close(): void;
  /** Libera el overlay y limpia sus handlers. */
  unmount(): void;
}

export interface ModalMountOptions {
  container: HTMLElement;
  iframe: HTMLIFrameElement;
  messenger: Messenger;
}

/** Monta el modal. No abre el overlay hasta llamar `open()`. */
export function mountModal(opts: ModalMountOptions): ModalMount {
  const { container, iframe, messenger } = opts;

  injectStyles();

  // --- DOM: overlay > card > (header > closeBtn) + (iframeWrapper > iframe) ---
  // The card is a flex column: the header stays pinned and the wrapper scrolls.
  const overlay = document.createElement('div');
  overlay.className = 'finva-modal-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', 'Solicitud de crédito Finva');
  overlay.setAttribute('data-open', 'false');
  // Inicia oculto hasta el primer open().
  overlay.style.display = 'none';

  const card = document.createElement('div');
  card.className = 'finva-modal-card';
  card.setAttribute('data-open', 'false');

  // El botón de cierre (X) lo renderiza la app DENTRO del iframe (esquina
  // superior derecha del contenido) y avisa vía `finva:close`. Aquí ya no hay
  // barra-header: evitábamos desperdiciar alto vertical con una franja para la
  // X. El cierre también funciona por backdrop, Escape y `finva:close`.
  const iframeWrapper = document.createElement('div');
  iframeWrapper.className = 'finva-modal-iframe-wrapper';

  // Mueve el iframe recibido dentro del wrapper (no crea uno nuevo).
  iframe.style.width = '100%';
  iframe.style.height = `${INITIAL_MIN_HEIGHT}px`;
  iframeWrapper.appendChild(iframe);

  card.appendChild(iframeWrapper);
  overlay.appendChild(card);
  document.body.appendChild(overlay);

  // --- Estado interno ---
  let disposed = false;
  let isOpen = false;
  let prevBodyOverflow = '';
  let scrollLocked = false;

  const trap = createFocusTrap(card);

  const offClose = messenger.on('finva:close', () => close());
  const offResize = messenger.on('finva:resize', (payload) => {
    const height = (payload as { height?: number } | null)?.height;
    if (typeof height === 'number' && height > 0 && iframe.isConnected) {
      // No cap: the iframe grows with its content and the wrapper scrolls once
      // the card reaches its max-height. Capping here would clip the form
      // instead, since the iframe has `scrolling="no"`.
      iframe.style.height = `${Math.ceil(height)}px`;
    }
  });

  // --- Utilidades internas ---

  function prefersReducedMotion(): boolean {
    return (
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    );
  }

  function lockScroll(): void {
    if (scrollLocked) return;
    prevBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    scrollLocked = true;
  }

  function unlockScroll(): void {
    if (!scrollLocked) return;
    document.body.style.overflow = prevBodyOverflow;
    prevBodyOverflow = '';
    scrollLocked = false;
  }

  // --- Cierre por backdrop click (click sobre el overlay, no sobre la card) ---
  function onOverlayClick(event: MouseEvent): void {
    if (event.target === overlay) close();
  }

  // --- Cierre por tecla Escape ---
  function onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
    }
  }

  // --- API pública del mount ---

  function open(): void {
    if (disposed || isOpen) return;
    isOpen = true;

    // Mostrar el overlay antes de marcar data-open para que la transición corra.
    overlay.style.display = '';
    // Reflow para garantizar que el navegador pinte el estado inicial
    // (data-open=false) antes de cambiar a true. Sin esto, la transición
    // puede no disparar al pasar de display:none al estado final.
    if (!prefersReducedMotion()) {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      overlay.offsetHeight;
    }

    overlay.setAttribute('data-open', 'true');
    card.setAttribute('data-open', 'true');

    lockScroll();
    trap.activate();
  }

  function close(): void {
    if (disposed || !isOpen) return;
    isOpen = false;

    overlay.setAttribute('data-open', 'false');
    card.setAttribute('data-open', 'false');

    trap.deactivate();
    unlockScroll();

    if (prefersReducedMotion()) {
      // Sin animación: ocultar inmediatamente.
      overlay.style.display = 'none';
      return;
    }

    // Esperar el fin de la transición de cierre antes de ocultar el overlay.
    // Fallback con setTimeout por si transitionend no dispara (p.ej. si otra
    // transición cancela, o si el navegador no lo emite en este contexto).
    let hidden = false;
    const hide = (): void => {
      if (hidden) return;
      hidden = true;
      overlay.style.display = 'none';
      card.removeEventListener('transitionend', onTransitionEnd);
    };
    const onTransitionEnd = (event: TransitionEvent): void => {
      // Solo nos interesa el fin de la transición de opacity/transform de la card.
      if (event.target === card && (event.propertyName === 'opacity' || event.propertyName === 'transform')) {
        hide();
      }
    };
    card.addEventListener('transitionend', onTransitionEnd);
    window.setTimeout(hide, CLOSE_TRANSITION_MS + 20);
  }

  function unmount(): void {
    if (disposed) return;
    disposed = true;

    isOpen = false;
    trap.deactivate();
    unlockScroll();

    offClose();
    offResize();

    overlay.removeEventListener('click', onOverlayClick);
    document.removeEventListener('keydown', onKeydown);

    overlay.remove();
    // Limpia el contenedor host por simetría con embedded (no deja huérfanos).
    emptyContainer(container);
  }

  // --- Cableado de listeners de cierre (backdrop, Escape, finva:close) ---
  // La X vive dentro del iframe (app) y cierra vía `finva:close` (ver `offClose`).
  overlay.addEventListener('click', onOverlayClick);
  // Escape se escucha en document (no en overlay) para atraparlo aunque el foco
  // esté temporalmente fuera del trap (p.ej. justo tras open antes de activate).
  document.addEventListener('keydown', onKeydown);

  return {
    get iframe(): HTMLIFrameElement | null {
      return iframe.isConnected ? iframe : null;
    },
    open,
    close,
    unmount,
  };
}